import { Capacitor, registerPlugin } from "@capacitor/core";
import type { ConsentState } from "./ConsentService";

declare global {
  interface Window {
    adsbygoogle: unknown[];
    __DIAMOND_RUSH_AD_TEST__?: {
      ready?: boolean;
      rewarded?: boolean;
    };
  }
}

export type RewardedPlacementId = "continue_run" | "double_gold" | "daily_vault_retry";
export type NativeConsentStatus = "OBTAINED" | "NOT_REQUIRED" | "REQUIRED" | "UNKNOWN";

export interface RewardedAdResult {
  rewarded: boolean;
  placementId: RewardedPlacementId;
}

interface NativeRevenuePlugin {
  initialize(options: { enabled: boolean }): Promise<{ consentStatus: NativeConsentStatus }>;
  loadRewardedAd(options: { placementId: RewardedPlacementId }): Promise<void>;
  showRewardedAd(options: { placementId: RewardedPlacementId }): Promise<{ rewarded: boolean }>;
  isRewardedAdReady(options: { placementId: RewardedPlacementId }): Promise<{ ready: boolean }>;
  showBanner(): Promise<void>;
  hideBanner(): Promise<void>;
}

const NativeRevenue = registerPlugin<NativeRevenuePlugin>("DiamondRushRevenue");

export interface AdConfig {
  enabled: boolean;
  complianceReady: boolean;
  certifiedCmpReady: boolean;
  clientId?: string;
  menuSlot?: string;
  campaignSlot?: string;
  admobEnabled?: boolean;
}

export interface AdPlatformOptions {
  native?: boolean;
  bridge?: NativeRevenuePlugin;
}

export class AdService {
  private initialized = false;
  private consentResolved = false;
  private personalizedAds = false;
  private readonly native: boolean;
  private readonly bridge: NativeRevenuePlugin;
  private readonly ready = new Set<RewardedPlacementId>();

  constructor(
    private readonly config: AdConfig,
    platform: AdPlatformOptions = {},
  ) {
    this.native = platform.native ?? Capacitor.isNativePlatform();
    this.bridge = platform.bridge ?? NativeRevenue;
  }

  async initializeNative(): Promise<NativeConsentStatus> {
    if (!this.native) return "NOT_REQUIRED";
    try {
      const result = await this.bridge.initialize({ enabled: this.config.admobEnabled === true });
      this.initialized = result.consentStatus !== "REQUIRED" && result.consentStatus !== "UNKNOWN";
      return result.consentStatus;
    } catch (error) {
      console.warn("Native revenue initialization unavailable", error);
      this.initialized = false;
      return "NOT_REQUIRED";
    }
  }

  async initialize(consent: ConsentState): Promise<void> {
    this.consentResolved = consent.choice !== "pending";
    this.personalizedAds = consent.categories.advertising === "granted";
    if (
      this.native
      || this.initialized
      || !this.consentResolved
      || !this.config.enabled
      || !this.validWebConfiguration()
    ) return;

    await this.loadScript();
    this.initialized = true;
    this.refreshVisibleSlots();
  }

  showPlacement(placement: "menu" | "campaign"): void {
    document.querySelectorAll<HTMLElement>("[data-ad-placement]").forEach((element) => {
      element.hidden = element.dataset.adPlacement !== placement
        || (!this.canShowWebAds() && !this.canShowLocalPreview());
    });
    if (this.native) {
      void this.bridge.showBanner().catch(() => undefined);
      return;
    }
    if (this.initialized) this.refreshVisibleSlots();
  }

  hideAll(): void {
    document.querySelectorAll<HTMLElement>("[data-ad-placement]").forEach((element) => {
      element.hidden = true;
    });
    if (this.native) void this.bridge.hideBanner().catch(() => undefined);
  }

  slotFor(placement: "menu" | "campaign"): string | undefined {
    return placement === "menu" ? this.config.menuSlot : this.config.campaignSlot;
  }

  canOfferRewarded(): boolean {
    if (typeof window !== "undefined" && window.__DIAMOND_RUSH_AD_TEST__) return true;
    return this.native && this.config.admobEnabled === true && this.initialized;
  }

  async loadRewardedAd(placementId: RewardedPlacementId): Promise<void> {
    if (typeof window !== "undefined" && window.__DIAMOND_RUSH_AD_TEST__) {
      this.ready.add(placementId);
      return;
    }
    if (!this.canOfferRewarded()) return;
    try {
      await this.withTimeout(this.bridge.loadRewardedAd({ placementId }), 8000);
      this.ready.add(placementId);
    } catch {
      this.ready.delete(placementId);
    }
  }

  isRewardedAdReady(placementId: RewardedPlacementId): boolean {
    if (typeof window !== "undefined" && window.__DIAMOND_RUSH_AD_TEST__) {
      return window.__DIAMOND_RUSH_AD_TEST__.ready !== false;
    }
    return this.ready.has(placementId);
  }

  async showRewardedAd(placementId: RewardedPlacementId): Promise<RewardedAdResult> {
    const test = typeof window !== "undefined" ? window.__DIAMOND_RUSH_AD_TEST__ : undefined;
    if (test) {
      this.ready.delete(placementId);
      return { rewarded: test.rewarded === true, placementId };
    }
    if (!this.canOfferRewarded() || !this.ready.has(placementId)) {
      return { rewarded: false, placementId };
    }
    this.ready.delete(placementId);
    try {
      const result = await this.withTimeout(
        this.bridge.showRewardedAd({ placementId }),
        8000,
      );
      return { rewarded: result.rewarded, placementId };
    } catch {
      return { rewarded: false, placementId };
    }
  }

  private canShowWebAds(): boolean {
    return this.initialized && this.consentResolved && this.config.enabled
      && this.validWebConfiguration();
  }

  private canShowLocalPreview(): boolean {
    if (this.native || typeof window === "undefined") return false;
    return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  }

  private validWebConfiguration(): boolean {
    return Boolean(
      !this.native
      && this.config.complianceReady
      && this.config.certifiedCmpReady
      && this.config.clientId?.startsWith("ca-pub-")
      && this.config.menuSlot
      && this.config.campaignSlot,
    );
  }

  private loadScript(): Promise<void> {
    const existing = document.querySelector<HTMLScriptElement>("script[data-diamond-rush-adsense]");
    if (existing) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.async = true;
      script.crossOrigin = "anonymous";
      script.dataset.diamondRushAdsense = "true";
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(this.config.clientId!)}`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("AdSense failed to load"));
      document.head.append(script);
    });
  }

  private refreshVisibleSlots(): void {
    window.adsbygoogle ??= [];
    document.querySelectorAll<HTMLElement>(".adsbygoogle:not([data-ad-requested])").forEach((slot) => {
      if (slot.closest<HTMLElement>("[data-ad-placement]")?.hidden) return;
      slot.dataset.adRequested = "true";
      window.adsbygoogle.push({
        requestNonPersonalizedAds: this.personalizedAds ? 0 : 1,
      });
    });
  }

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        window.setTimeout(() => reject(new Error("Ad request timed out")), timeoutMs);
      }),
    ]);
  }
}
